import type React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";

import { useNavigate, useLocation, useParams } from "react-router-dom";
import pattern from "../../../assets/pattern.png";
import Muzukuruimg3 from "../../../assets/Muzukuruimg3.png";
import logoBrown from "../../../assets/logoBrown.png";
import apiService from "../../../service/Api";
import { RootState } from "../../../store/Index";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import CustomTextField from "../../../components/common/Textfield";
import { BookingResponse } from "../types/view-booking";

const InternetAgreementForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const bookingId = id || location.state?.bookingId;

  const [formData, setFormData] = useState({
    client_name_one: "",
    client_name_two: "",
    signature: "",
    date_signed: "",
  });

  const [bookingData, setBookingData] = useState<BookingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch booking data
  useEffect(() => {
    const fetchBookingData = async () => {
      if (!bookingId) {
        setError("No booking ID provided");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const bookingResponse = await apiService.get<BookingResponse>(
          `/api/bookings/booking/${bookingId}/`
        );
        const booking = bookingResponse.data;

        setBookingData(booking);

        const clientFullName = booking.contact
          ? `${booking.contact.first_name || ""} ${
              booking.contact.last_name || ""
            }`.trim()
          : "";

        setFormData((prev) => ({
          ...prev,
          client_name_one: clientFullName,
          client_name_two: clientFullName,
        }));

        setError(null);
      } catch (err: any) {
        let errorMessage = "Failed to load booking data. Please try again.";
        if (err.response?.status === 500) {
          errorMessage =
            "Server error: Unable to retrieve data. Please try again later or contact support.";
        } else if (err.response?.status === 401) {
          errorMessage = "Unauthorized: Please log in again.";
          navigate("/login");
        } else if (err.response?.status === 403) {
          errorMessage =
            "Permission denied: You do not have access to this booking.";
          navigate("/bookings");
        } else if (err.response?.data?.detail) {
          errorMessage = err.response.data.detail;
        }
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
        console.error("Fetch error:", err.response?.data, err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBookingData();
  }, [bookingId, navigate]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clientName = bookingData?.contact
    ? `${bookingData.contact.first_name || ""} ${
        bookingData.contact.last_name || ""
      }`.trim()
    : "";

  const clientEmail = bookingData?.contact?.email || "";

  const clientPhone = bookingData?.contact?.phone || "";

  const eventDate =
    bookingData?.services?.[0]?.date_of_service ||
    bookingData?.events?.[0]?.start_date_of_event ||
    "";

  const venue = bookingData?.services?.[0]?.venue?.name || "";

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading booking data...</Typography>
      </Box>
    );
  }

  return (
    <>
      <style>
        {`
          @media print {
            .bottom-logo {
              display: none !important;
            }
          }
        `}
      </style>
      <Box
        className="print-container"
        sx={{
          p: 1,

          color: reduxTheme.primary_colors?.[700] || "#614438",
        }}
      >
        {/* Main Content */}
        <Box
          sx={{
            backgroundSize: "20px 20px",
            backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
            minHeight: "100vh",
            color: reduxTheme.primary_colors?.[400] || "#614438",
            padding: "20px",
            backgroundImage: `url(${pattern})`,

            "@media print": {
              overflow: "visible",
              height: "auto",
              backgroundAttachment: "scroll",
              minHeight: "82vh",
            },
          }}
        >
          <Container
            maxWidth="md"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Paper
              elevation={5}
              sx={{
                overflow: "visible",
                width: "100%",
                mb: 2,
                "@media print": {
                  overflow: "visible",
                  boxShadow: "none",
                  margin: "0 !important",
                  pageBreakInside: "avoid",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-end",
                  height: "auto",
                  width: "100%",
                  mt: 2,
                }}
              >
                <img
                  alt="muzukuru"
                  width="240px"
                  height="80px"
                  src={logoBrown || "/placeholder.svg"}
                  style={{ objectFit: "contain" }}
                />
              </Box>
              {/* Header Bar */}
              <Box
                sx={{
                  bgcolor: reduxTheme.primary_colors?.[400] || "#614438",

                  color: reduxTheme.primary_colors?.[900] || "#ffffff",
                  p: 0,
                  backgroundImage: "url(/static/images/pattern.png)",
                  backgroundSize: "20px 20px",
                  borderBottom: "3px solid black",
                  position: "relative",
                  height: "20px",
                }}
              />

              {/* Form Content */}
              <Box
                component="form"
                sx={{
                  p: 3,
                  backgroundImage: "url(/static/images/pattern.png)",
                  backgroundSize: "20px 20px",
                }}
              >
                <Typography
                  variant="h4"
                  align="center"
                  sx={{
                    mt: 1,
                    fontWeight: "bold",
                    mb: 3,
                    color: reduxTheme.primary_colors?.[400] || "#614438",
                  }}
                >
                  Internet Agreement & Copyright Disclaimer Form
                </Typography>

                {/* Client Information Section */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: 1,
                    mb: 2,
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}
                  >
                    Client Name:
                  </Typography>
                  <CustomTextField
                    name="client_name"
                    margin="normal"
                    variant="standard"
                    value={clientName || ""}
                    sx={{ width: "300px", mr: 4 }}
                    InputProps={{ readOnly: true }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}
                  >
                    Client Contact:
                  </Typography>
                  <CustomTextField
                    name="client_phone"
                    margin="normal"
                    variant="standard"
                    value={clientPhone || ""}
                    sx={{ width: "300px" }}
                    InputProps={{ readOnly: true }}
                  />
                </Box>

                <Box
                  sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}
                  >
                    Email Address:
                  </Typography>
                  <CustomTextField
                    name="client_email"
                    margin="normal"
                    variant="standard"
                    value={clientEmail || ""}
                    sx={{ width: "300px", mr: 4 }}
                    InputProps={{ readOnly: true }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}
                  >
                    Event Date:
                  </Typography>
                  <CustomTextField
                    name="service_date"
                    margin="normal"
                    variant="standard"
                    value={
                      eventDate ? new Date(eventDate).toLocaleDateString() : ""
                    }
                    sx={{ width: "300px" }}
                    InputProps={{ readOnly: true }}
                  />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ mr: 1, fontWeight: "bold" }}
                  >
                    Venue:
                  </Typography>
                  <CustomTextField
                    name="service_venue"
                    margin="normal"
                    variant="standard"
                    value={venue || ""}
                    sx={{ width: "700px" }}
                    InputProps={{ readOnly: true }}
                  />
                </Box>

                {/* Internet Agreement Section */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mt: 3,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      color: reduxTheme.primary_colors?.[400] || "#614438",
                      fontWeight: "bold",
                      mb: 1,
                    }}
                  >
                    INTERNET
                  </Typography>
                </Box>

                <Box sx={{ mt: 1, mb: 2 }}>
                  <Typography paragraph>
                    As Muzukuru Livestreaming Company, we advise utilizing a{" "}
                    <strong> wired ethernet connection </strong> to maximize the
                    stability of the internet connection during the broadcast.
                    While we recommend this, please note that even with a wired
                    connection,
                    <strong> interruptions may still occur.</strong>
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography>I,</Typography>
                    <CustomTextField
                      name="client_name_one"
                      variant="standard"
                      onChange={(e) =>
                        handleInputChange("client_name_one", e.target.value)
                      }
                      sx={{ mx: 1, minWidth: 200 }}
                    />
                    <Typography>
                      fully understand that while a wired internet connection
                      can
                    </Typography>
                  </Box>

                  <Typography paragraph>
                    enhance stability, it is not infallible and may experience
                    disruptions, potentially affecting the broadcast quality.
                  </Typography>
                </Box>

                {/* Copyright Disclaimer Section */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      color: reduxTheme.primary_colors?.[400] || "#614438",
                      fontWeight: "bold",
                      mb: 1,
                    }}
                  >
                    COPYRIGHT DISCLAIMER
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography>I,</Typography>
                  <CustomTextField
                    name="client_name_two"
                    variant="standard"
                    onChange={(e) =>
                      handleInputChange("client_name_two", e.target.value)
                    }
                    sx={{ mx: 1, minWidth: 200 }}
                  />
                  <Typography>
                    acknowledge that playing copyrighted music during the
                    livestream without
                  </Typography>
                </Box>

                <Typography paragraph>
                  proper licensing or permission may lead to copyright strikes
                  on platforms such as YouTube. The Livestreaming Company will
                  not be held liable for any copyright infringements occurring
                  during the broadcast.
                </Typography>

                <Typography paragraph>
                  Furthermore, I understand that segments of the livestream
                  containing copyrighted music may be muted, and the video might
                  be made unavailable by YouTube due to copyright infringement
                  claims.
                </Typography>

                {/* Signature Section */}
                <Box sx={{ display: "flex", alignItems: "center", mt: 3 }}>
                  <Typography
                    variant="body2"
                    sx={{ mr: 1, fontWeight: "bold" }}
                  >
                    Signed:
                  </Typography>
                  <CustomTextField
                    name="signature"
                    margin="normal"
                    variant="standard"
                    value={formData.signature}
                    onChange={(e) =>
                      handleInputChange("signature", e.target.value)
                    }
                    sx={{ width: "200px" }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ mr: 1, ml: 20, fontWeight: "bold" }}
                  >
                    Date:
                  </Typography>
                  <CustomTextField
                    name="date_signed"
                    margin="normal"
                    variant="standard"
                    value={formData.date_signed}
                    onChange={(e) =>
                      handleInputChange("date_signed", e.target.value)
                    }
                    sx={{ width: "200px" }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Box>
              {/* Bottom Logo */}
              <Box
                className="bottom-logo"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-end",
                  height: "auto",
                  width: "100%",
                  mt: 2,
                }}
              >
                <img
                  alt="Muzukuruimg3"
                  width="180px"
                  height="80px"
                  src={Muzukuruimg3 || "/placeholder.svg"}
                  style={{ objectFit: "contain" }}
                />
              </Box>
            </Paper>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default InternetAgreementForm;
